import { isWithinEasternGalilee } from '@/lib/map/eastern-galilee';

/** Planning lot (מגרש) from compilation / GIS-Net — not cadastral גוש/חלקה. */
export function normalizeMigrash(value: string): string {
  return value.trim().replace(/\s+/g, ' ');
}

/** GovMap-style גוש/חלקה — not a committee planning-lot (מגרש). */
export function isCadastralOnlyLabel(value: string): boolean {
  const n = normalizeMigrash(value);
  return /^(\d{1,6})[/-](\d{1,4})$/.test(n);
}

export function isValidMigrash(value: string): boolean {
  const n = normalizeMigrash(value);
  if (n.length < 1 || n.length > 64) return false;
  if (isCadastralOnlyLabel(n)) return false;
  return /^[\d\s()."'/\-א-תa-zA-Z]+$/.test(n);
}

/** Internal map-pick key (designation@lat,lng or pick@lat,lng) — not a committee migrash. */
export function isParcelPickKey(value: string): boolean {
  const n = normalizeMigrash(value);
  return /^pick@/i.test(n) || /@\d/.test(n);
}

/** Statutory / national plan numbers (תמל/…, 259-…, ג/…) — not committee migrash labels. */
export function isPlanNumberLabel(value: string): boolean {
  const t = normalizeMigrash(value)
    .replace(/תמ["']?ל/gi, 'תמל')
    .replace(/\s*\/\s*/g, '/')
    .replace(/\s+/g, ' ');
  if (!t || t === '—') return false;
  if (/^pick@/i.test(t) || t.includes('@')) return false;
  if (/^\d{2,3}-\d{4,}$/.test(t)) return true;
  if (/^תמל\/\d+/i.test(t)) return true;
  if (/^[\u0590-\u05FF]{1,4}\/[\dא-ת]+/.test(t)) return true;
  return false;
}

/** True when value is a committee planning-lot label suitable for "מספר מגרש" display. */
export function isCommitteeMigrashLabel(value: string | null | undefined): boolean {
  if (!value?.trim()) return false;
  const n = normalizeMigrash(value);
  return isValidMigrash(n) && !isParcelPickKey(n) && !isPlanNumberLabel(n);
}

/** @deprecated Cadastral only — kept for optional VGM backfill display. */
export function isValidGush(value: string): boolean {
  return /^\d{1,6}$/.test(value.trim());
}

/** @deprecated Cadastral only. */
export function isValidHelka(value: string): boolean {
  return /^\d{1,4}$/.test(value.trim());
}

export function isValidMapPoint(lat: number | undefined, lng: number | undefined): boolean {
  if (lat == null || lng == null || !Number.isFinite(lat) || !Number.isFinite(lng)) {
    return false;
  }
  return isWithinEasternGalilee(lat, lng);
}

/** Map pick on XPLAN ייעודי קרקע — designation + coordinates (migrash is server-derived plan key). */
export function hasWizardParcelSelection(answers: {
  migrash?: string;
  designation?: string;
  map_lat?: number;
  map_lng?: number;
}): boolean {
  return (
    isValidMapPoint(answers.map_lat, answers.map_lng) &&
    Boolean(answers.designation?.trim()) &&
    Boolean(answers.migrash?.trim())
  );
}
